"use client";

import { useEffect, useRef, useState } from "react";
import { CopyButton, Notice } from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA_PROSE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  ConsonantTable,
  KeyRow,
  type PickedKey,
  VowelTable,
} from "@/components/tools/word-knowledge/ipa-keys";
import { t } from "@/lib/i18n/interpolate";
import {
  CLICKS,
  codePointLabels,
  DIACRITICS,
  deleteBefore,
  EJECTIVE,
  EXTRA_LETTERS,
  IMPLOSIVES,
  insertSymbol,
  OTHER_SYMBOLS,
  SUPRASEGMENTALS,
  TIE_BARS,
  TONE_ACCENTS,
  TONE_LETTERS,
} from "@/lib/tool-kit/word-knowledge/ipa-chart";

/** English strings. A translated content file overrides these through `ui`. */
export const IPA_KEYBOARD_STRINGS = {
  textLabel: "Your IPA text",
  textPlaceholder: "Click a symbol below, or type here…",
  textHint:
    "A symbol goes in at the cursor. A diacritic goes on the character before the cursor. Hover a key for its name and code point.",
  copy: "Copy text",
  copied: "Copied!",
  backspace: "Delete last symbol",
  clear: "Clear",
  needBase:
    "Type a base symbol first: a diacritic attaches to the character before the cursor.",
  inserted: "Inserted {symbol} ({codes}): {name}",
  codePointsHeading: "Code points in your text",
  codePointsMore: "and {count} more",
  noText: "Nothing yet. Click a key to start.",
  headings: {
    pulmonic: "Consonants (pulmonic)",
    nonPulmonic: "Consonants (non-pulmonic)",
    clicks: "Clicks",
    implosives: "Voiced implosives",
    ejectives: "Ejectives",
    other: "Other symbols and tie bars",
    vowels: "Vowels",
    diacritics: "Diacritics",
    suprasegmentals: "Suprasegmentals",
    tones: "Tones and word accents",
    toneLetters: "Tone letters",
    extras: "Also used, but not on the chart",
  },
  hints: {
    pulmonic:
      "Rows are manners of articulation and columns are places. Where symbols appear in pairs, the one on the right is voiced.",
    vowels:
      "Height runs down and backness across. Where symbols appear in pairs, the one on the right is rounded.",
    diacritics:
      "The dotted circle stands for the character the mark attaches to. The ring above (◌̊) is for letters with a descender, such as ɡ.",
    tones: "Accents sit on a vowel; tone letters stand alone after a syllable.",
    extras:
      "Letters common in dictionaries and teaching materials that the chart itself leaves out.",
  },
  places: {
    bilabial: "Bilabial",
    labiodental: "Labiodental",
    dental: "Dental",
    alveolar: "Alveolar",
    postalveolar: "Postalveolar",
    retroflex: "Retroflex",
    palatal: "Palatal",
    velar: "Velar",
    uvular: "Uvular",
    pharyngeal: "Pharyngeal",
    glottal: "Glottal",
  },
  manners: {
    plosive: "Plosive",
    nasal: "Nasal",
    trill: "Trill",
    tap: "Tap or flap",
    fricative: "Fricative",
    lateralFricative: "Lateral fricative",
    approximant: "Approximant",
    lateralApproximant: "Lateral approximant",
  },
  heights: {
    close: "Close",
    nearClose: "Near-close",
    closeMid: "Close-mid",
    mid: "Mid",
    openMid: "Open-mid",
    nearOpen: "Near-open",
    open: "Open",
  },
  backness: {
    front: "Front",
    nearFront: "Near-front",
    central: "Central",
    nearBack: "Near-back",
    back: "Back",
  },
  voiceless: "voiceless",
  voiced: "voiced",
  unrounded: "unrounded",
  rounded: "rounded",
  consonantName: "{voicing} {place} {manner}",
  vowelName: "{height} {backness} {rounding} vowel",
  rowHeader: "Manner of articulation",
  columnHeader: "Vowel height",
  names: {
    clickBilabial: "bilabial click",
    clickDental: "dental click",
    clickAlveolar: "(post)alveolar click",
    clickPalatoalveolar: "palatoalveolar click",
    clickLateral: "alveolar lateral click",
    implosiveBilabial: "voiced bilabial implosive",
    implosiveDental: "voiced dental or alveolar implosive",
    implosivePalatal: "voiced palatal implosive",
    implosiveVelar: "voiced velar implosive",
    implosiveUvular: "voiced uvular implosive",
    ejective: "ejective",
    labialVelarFricative: "voiceless labial-velar fricative",
    labialVelarApproximant: "voiced labial-velar approximant",
    labialPalatalApproximant: "voiced labial-palatal approximant",
    epiglottalFricative: "voiceless epiglottal fricative",
    epiglottalVoicedFricative: "voiced epiglottal fricative",
    epiglottalPlosive: "epiglottal plosive",
    alveoloPalatalFricative: "voiceless alveolo-palatal fricative",
    alveoloPalatalVoicedFricative: "voiced alveolo-palatal fricative",
    alveolarLateralFlap: "voiced alveolar lateral flap",
    simultaneousPostalveolarVelar:
      "simultaneous postalveolar and velar fricative",
    tieAbove: "tie bar above (affricates and double articulations)",
    tieBelow: "tie bar below",
    rColouredSchwa: "r-coloured schwa",
    rColouredOpenMid: "r-coloured open-mid central vowel",
    velarizedL: "velarized alveolar lateral approximant",
    voiceless: "voiceless (ring below)",
    voicelessAbove: "voiceless (ring above)",
    voiced: "voiced",
    aspirated: "aspirated",
    moreRounded: "more rounded",
    lessRounded: "less rounded",
    advanced: "advanced",
    retracted: "retracted",
    centralized: "centralized",
    midCentralized: "mid-centralized",
    syllabic: "syllabic (below)",
    syllabicAbove: "syllabic (above)",
    nonSyllabic: "non-syllabic (below)",
    nonSyllabicAbove: "non-syllabic (above)",
    rhoticity: "rhoticity",
    breathy: "breathy voiced",
    creaky: "creaky voiced",
    linguolabial: "linguolabial",
    labialized: "labialized",
    palatalized: "palatalized",
    velarized: "velarized",
    pharyngealized: "pharyngealized",
    velarizedOrPharyngealized: "velarized or pharyngealized",
    raised: "raised",
    lowered: "lowered",
    advancedTongueRoot: "advanced tongue root",
    retractedTongueRoot: "retracted tongue root",
    dental: "dental",
    apical: "apical",
    laminal: "laminal",
    nasalized: "nasalized",
    nasalRelease: "nasal release",
    lateralRelease: "lateral release",
    noAudibleRelease: "no audible release",
    primaryStress: "primary stress",
    secondaryStress: "secondary stress",
    long: "long",
    halfLong: "half-long",
    extraShort: "extra-short",
    minorGroup: "minor (foot) group",
    majorGroup: "major (intonation) group",
    syllableBreak: "syllable break",
    linking: "linking (absence of a break)",
    accentExtraHigh: "extra-high tone",
    accentHigh: "high tone",
    accentMid: "mid tone",
    accentLow: "low tone",
    accentExtraLow: "extra-low tone",
    accentRising: "rising tone",
    accentFalling: "falling tone",
    accentHighRising: "high rising tone",
    accentLowRising: "low rising tone",
    accentRisingFalling: "rising-falling tone",
    downstep: "downstep",
    upstep: "upstep",
    globalRise: "global rise",
    globalFall: "global fall",
    toneExtraHigh: "extra-high tone letter",
    toneHigh: "high tone letter",
    toneMid: "mid tone letter",
    toneLow: "low tone letter",
    toneExtraLow: "extra-low tone letter",
  } as Record<string, string>,
  source:
    "Layout and symbol groups follow the IPA chart (revised to 2020), © International Phonetic Association, licensed CC BY-SA 3.0. CitationEasy is not endorsed by the Association.",
} as const;

const MAX_TEXT = 2000;
const MAX_CODES = 40;

interface Status {
  kind: "inserted" | "needBase";
  key?: PickedKey;
}

export default function IpaKeyboardWidget() {
  const strings = useToolUi(IPA_KEYBOARD_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [last, setLast] = useState<Status | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const caret = useRef<number | null>(null);

  // Put the cursor after the inserted symbol once React has written the new text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs after each text change
  useEffect(() => {
    if (caret.current !== null && box.current) {
      box.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  }, [text]);

  function selection(): [number, number] {
    const element = box.current;
    return [
      element?.selectionStart ?? text.length,
      element?.selectionEnd ?? text.length,
    ];
  }

  function pick(key: PickedKey) {
    const [start, end] = selection();
    const edit = insertSymbol(text, start, end, key.text, key.combining);
    if (!edit || edit.text.length > MAX_TEXT) {
      setLast({ kind: "needBase" });
      return;
    }
    caret.current = edit.caret;
    setText(edit.text);
    setLast({ kind: "inserted", key });
  }

  function backspace() {
    const [start, end] = selection();
    const edit = deleteBefore(text, start, end);
    caret.current = edit.caret;
    setText(edit.text);
    setLast(null);
  }

  const codes = codePointLabels(text);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className={LABEL} htmlFor="ipa-text">
          {strings.textLabel}
        </label>
        <textarea
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className={TEXTAREA_PROSE}
          id="ipa-text"
          lang="und-fonipa"
          onChange={(event) => setText(event.target.value.slice(0, MAX_TEXT))}
          placeholder={strings.textPlaceholder}
          ref={box}
          rows={3}
          spellCheck={false}
          value={text}
        />
        <p className={HINT}>{strings.textHint}</p>
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={strings.copied}
            disabled={text === ""}
            label={strings.copy}
            small
            text={text}
          />
          <button
            className={BUTTON_SMALL}
            disabled={text === ""}
            onClick={backspace}
            type="button"
          >
            {strings.backspace}
          </button>
          <button
            className={BUTTON_SMALL}
            disabled={text === ""}
            onClick={() => {
              setText("");
              setLast(null);
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <div aria-live="polite" className="min-h-6 text-sm">
          {last?.kind === "needBase" ? (
            <Notice>{strings.needBase}</Notice>
          ) : null}
          {last?.kind === "inserted" && last.key ? (
            <p className="text-muted-foreground">
              {t(strings.inserted, {
                symbol: last.key.text,
                codes: codePointLabels(last.key.text).join(" "),
                name: last.key.name,
              })}
            </p>
          ) : null}
        </div>
      </div>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.pulmonic}</h2>
        <p className={HINT}>{strings.hints.pulmonic}</p>
        <ConsonantTable onPick={pick} strings={strings} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.nonPulmonic}</h2>
        <h3 className={WIDGET_H3}>{strings.headings.clicks}</h3>
        <KeyRow onPick={pick} strings={strings} symbols={CLICKS} />
        <h3 className={WIDGET_H3}>{strings.headings.implosives}</h3>
        <KeyRow onPick={pick} strings={strings} symbols={IMPLOSIVES} />
        <h3 className={WIDGET_H3}>{strings.headings.ejectives}</h3>
        <KeyRow onPick={pick} strings={strings} symbols={[EJECTIVE]} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.other}</h2>
        <KeyRow
          onPick={pick}
          strings={strings}
          symbols={[...OTHER_SYMBOLS, ...TIE_BARS]}
        />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.vowels}</h2>
        <p className={HINT}>{strings.hints.vowels}</p>
        <VowelTable onPick={pick} strings={strings} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.diacritics}</h2>
        <p className={HINT}>{strings.hints.diacritics}</p>
        <KeyRow onPick={pick} strings={strings} symbols={DIACRITICS} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.suprasegmentals}</h2>
        <KeyRow onPick={pick} strings={strings} symbols={SUPRASEGMENTALS} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.tones}</h2>
        <p className={HINT}>{strings.hints.tones}</p>
        <KeyRow onPick={pick} strings={strings} symbols={TONE_ACCENTS} />
        <h3 className={WIDGET_H3}>{strings.headings.toneLetters}</h3>
        <KeyRow onPick={pick} strings={strings} symbols={TONE_LETTERS} />
      </section>

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.headings.extras}</h2>
        <p className={HINT}>{strings.hints.extras}</p>
        <KeyRow onPick={pick} strings={strings} symbols={EXTRA_LETTERS} />
      </section>

      <section aria-live="polite" className="space-y-2">
        <h2 className={WIDGET_H3}>{strings.codePointsHeading}</h2>
        {codes.length === 0 ? (
          <p className={HINT}>{strings.noText}</p>
        ) : (
          <p className="break-words font-mono text-muted-foreground text-xs">
            {codes.slice(0, MAX_CODES).join(" ")}
            {codes.length > MAX_CODES
              ? ` … ${t(strings.codePointsMore, { count: fmt(codes.length - MAX_CODES) })}`
              : ""}
          </p>
        )}
      </section>
      <p className={HINT}>{strings.source}</p>
    </div>
  );
}
